import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

import { ZeosSpinner } from "../../../components/Shared/ZeosSpinner";
import {
  exploreSurveyConfigBySlug,
  exploreSurveyIntentPath,
  IExploreSurveyDomainConfig,
} from "../../../configs/ExploreSurveyConfig";
import { FeatureFlags } from "../../../configs/FeatureFlags";
import { AuthContext } from "../../../context/AuthContext";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { createLogger } from "../../../services/Logger";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import { hydrateSurveyAnswers } from "../../../services/survey/SurveyAnswerHelpers";
import { isQuestionType } from "../../../services/survey/SurveyVisibilityHelpers";
import {
  ISurveyContract,
  ISurveyUpdateContract,
} from "../../../services/zeos-api/contracts/Survey";
import {
  isComprehensiveCompletion,
  QuestionTypeEnum,
} from "../../../services/zeos-api/enums/Survey";
import { DashboardFooter } from "../layout/DashboardFooter";
import { DashboardHeader } from "../layout/DashboardHeader";
import {
  ExploreSurveyChrome,
  ExploreSurveyResourcesSidebar,
} from "./ExploreSurveyChrome";
import { ExploreSurveyShell } from "./ExploreSurveyShell";

interface IProps {
  domainConfig?: IExploreSurveyDomainConfig;
}

export const ExploreSurveyGoals: React.FC<IProps> = (props) => {
  const params = useParams<{ domain?: string }>();
  const config =
    props.domainConfig ?? exploreSurveyConfigBySlug(params.domain);
  const navigate = useNavigate();
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const log = useMemo(
    () => createLogger(`ExploreSurveyGoals:${config?.slug ?? "unknown"}`),
    [config?.slug]
  );
  const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";

  const [template, setTemplate] = useState<ISurveyContract | null>(null);
  const [survey, setSurvey] = useState<ISurveyUpdateContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config) {
      setLoading(false);
      setError("Unknown explore survey domain.");
      return;
    }

    let cancelled = false;
    const loadAsync = async () => {
      // Domain switch reuses this component — clear prior domain's survey first.
      setLoading(true);
      setError(null);
      setTemplate(null);
      setSurvey(null);

      try {
        const templateResponse = await api.Survey.getSurveyTemplate(
          config.templateId
        );
        if (!templateResponse?.data) {
          setError("Unable to load survey template.");
          return;
        }
        if (cancelled) {
          return;
        }
        setTemplate(templateResponse.data);

        const zeosUser = await auth.zeosUserAsync();
        if (!zeosUser?.zeosUserId) {
          setError("User not found.");
          return;
        }
        const surveyResponse = await api.Survey.getUserSurvey(
          templateResponse.data.surveyTemplateId,
          zeosUser.zeosUserId
        );
        if (cancelled) {
          return;
        }
        const status = (surveyResponse?.zeosStatus || "").toLowerCase();
        if (
          status === "error" &&
          !surveyResponse?.data &&
          (surveyResponse?.message || "")
            .toLowerCase()
            .includes("no survey instance")
        ) {
          setSurvey(null);
          return;
        }
        setSurvey(surveyResponse?.data ?? null);
      } catch (err) {
        log.error("Failed to load explore survey goals", err);
        setError("Unable to load survey profile.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAsync();
    return () => {
      cancelled = true;
    };
  }, [api.Survey, auth, config?.slug, config?.templateId, log]);

  const hasComprehensiveSurvey = isComprehensiveCompletion(
    survey?.completionMode
  );

  const summarySections = useMemo(() => {
    if (!template || !survey?.answers?.length || !hasComprehensiveSurvey) {
      return [];
    }
    const hydrated = hydrateSurveyAnswers(template, survey.answers);

    return template.questionnaires
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((questionnaire) => {
        const items = questionnaire.questions
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((question) => {
            const value = hydrated[question.questionId];
            if (value == null || value === "") {
              return null;
            }
            if (
              isQuestionType(question.questionTypeId, QuestionTypeEnum.FreeText)
            ) {
              return {
                question,
                answerLabel: String(value),
              };
            }
            const selectedIds = Array.isArray(value)
              ? value.map((id) => Number(id))
              : [Number(value)];
            const labels = selectedIds
              .map((id, index) => {
                const opt = question.options.find(
                  (o) => Number(o.responseOptionId) === id
                );
                if (!opt) {
                  return null;
                }
                if (
                  isQuestionType(
                    question.questionTypeId,
                    QuestionTypeEnum.RankedChoice
                  )
                ) {
                  const rank =
                    index === 0
                      ? "Primary"
                      : index === 1
                        ? "Secondary"
                        : index === 2
                          ? "Tertiary"
                          : `Rank ${index + 1}`;
                  return `${rank}: ${opt.displayName}`;
                }
                return opt.displayName;
              })
              .filter(Boolean);
            if (!labels.length) {
              return null;
            }
            return {
              question,
              answerLabel: labels.join(", "),
            };
          })
          .filter(Boolean) as Array<{
          question: { displayName: string };
          answerLabel: string;
        }>;

        if (!items.length) {
          return null;
        }
        return {
          title: questionnaire.displayName,
          items,
        };
      })
      .filter(Boolean) as Array<{
      title: string;
      items: Array<{ question: { displayName: string }; answerLabel: string }>;
    }>;
  }, [hasComprehensiveSurvey, survey?.answers, template]);

  if (!config) {
    return (
      <div className="w-screen min-h-screen bg-zeos-white-md flex flex-col">
        <DashboardHeader />
        <div className="flex-1 flex items-center justify-center p-8">
          Unknown explore survey.
        </div>
        <DashboardFooter />
      </div>
    );
  }

  if (!loading && !error && (!survey || !hasComprehensiveSurvey)) {
    return <ExploreSurveyShell domainConfig={config} />;
  }

  return (
    <div className="w-screen min-h-screen bg-zeos-white-md flex flex-col">
      <DashboardHeader />
      <div className="flex-1 w-full">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-zeos-black">
              {config.breadcrumb}
              <div className="w-6 h-1 bg-zeos-orange-dk mt-2" />
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="border border-gray-300 rounded-md px-4 py-2 text-sm w-64 bg-white"
              />
              <FaSearch className="absolute right-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 mt-6">
            <div
              className={`col-span-12 space-y-6 ${
                FeatureFlags.enableExploreResourcesSidebar ? "lg:col-span-9" : ""
              }`}
            >
              <ExploreSurveyChrome
                activeSlug={config.slug}
                stepLabels={
                  template
                    ? template.questionnaires
                        .slice()
                        .sort(
                          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
                        )
                        .map((qn) => qn.displayName)
                        .concat(
                          template.questionnaires.some((qn) =>
                            (qn.displayName || "")
                              .toLowerCase()
                              .includes("summary")
                          )
                            ? []
                            : ["Summary"]
                        )
                    : ["Summary"]
                }
                activeStepIndex={
                  template
                    ? Math.max(
                        0,
                        (template.questionnaires.some((qn) =>
                          (qn.displayName || "")
                            .toLowerCase()
                            .includes("summary")
                        )
                          ? template.questionnaires.length
                          : template.questionnaires.length + 1) - 1
                      )
                    : 0
                }
              />

              <div className="bg-white border rounded p-6 space-y-6">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <ZeosSpinner />
                  </div>
                ) : error ? (
                  <div className="text-red-600">{error}</div>
                ) : (
                  <>
                    <div>
                      <h2 className="text-2xl font-semibold text-zeos-black">
                        {config.summaryTitle}
                      </h2>
                      <p className="text-zeos-grey-md mt-2">
                        {config.summaryBody}
                      </p>
                    </div>

                    <div className="space-y-6">
                      {summarySections.map((section) => (
                        <div key={section.title} className="space-y-3">
                          <div className="text-sm font-semibold text-gray-700">
                            {section.title}
                          </div>
                          <div className="space-y-3">
                            {section.items.map((item) => (
                              <div
                                key={item.question.displayName}
                                className="border-l-4 border-zeos-orange-md pl-4 py-2"
                              >
                                <div className="text-sm font-semibold text-zeos-black">
                                  {item.question.displayName}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {item.answerLabel}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        className="px-6 py-2 rounded bg-zeos-orange-dk text-white text-sm"
                        onClick={() =>
                          navigate(
                            exploreSurveyIntentPath(exploreSlug, config.slug)
                          )
                        }
                      >
                        Re-take Survey
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {FeatureFlags.enableExploreResourcesSidebar && (
              <ExploreSurveyResourcesSidebar />
            )}
          </div>
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
};
